import { create } from 'zustand';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  locale: string;
  timezone: string;
  email_verified: boolean;
  mfa_enabled: boolean;
  avatar_url: string | null;
  companies: { id: string; name: string; slug: string; status: string }[];
}

interface AuthState {
  /**
   * **In memory only, never persisted.**
   *
   * The refresh token already lives where it should — an `HttpOnly` cookie under
   * the `__Host-` prefix, with double-submit CSRF (block 1C). Putting the access
   * token in `localStorage` would undo that: an XSS that cannot read the cookie
   * could read the storage. The cost is one `POST /auth/refresh` on every hard
   * reload, which the root route waits for behind a skeleton.
   *
   * La regla es sobre credenciales, no sobre el disco: `i18n/locale.ts` y
   * `stores/theme.ts` sí guardan idioma y tema en `localStorage`, porque son
   * preferencias y porque sin ese espejo la aplicación arrancaría en el idioma
   * y el tema equivocados durante todo el `refresh` de arriba.
   *
   * El token sí viaja entre pestañas por `BroadcastChannel`
   * (`api/refresh-coordinator.ts`): eso es memoria a memoria dentro de la misma
   * sesión de navegador y del mismo origen, no un almacén que un XSS pueda leer
   * más tarde, así que no contradice esta regla — la hace cumplible, porque sin
   * él cada pestaña gastaría la cookie de refresh por su cuenta.
   */
  accessToken: string | null;
  user: CurrentUser | null;
  /** False until the boot refresh has finished, so the router does not flash a login screen. */
  ready: boolean;

  signIn: (token: string, user: CurrentUser) => void;
  setUser: (user: CurrentUser) => void;
  setToken: (token: string | null) => void;
  markReady: () => void;
  signOut: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  ready: false,

  signIn: (accessToken, user) => set({ accessToken, user, ready: true }),
  setUser: (user) => set({ user }),
  setToken: (accessToken) => set({ accessToken }),
  markReady: () => set({ ready: true }),
  signOut: () => set({ accessToken: null, user: null, ready: true }),
}));

/**
 * Read outside React — `authFetch` needs the token on every request and must not
 * be a hook.
 */
export const authState = {
  token: () => useAuth.getState().accessToken,
  set: (token: string | null) => useAuth.getState().setToken(token),
  clear: () => useAuth.getState().signOut(),
};
